import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

interface TourStep {
  el: string;
  title: string;
  description: string;
  /** Popover placement, only where driver.js's default collides with something. */
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
}

const TOUR_STEPS: TourStep[] = [
  { el: '[data-tour="inputs"]', title: 'Your assumptions', description: 'Every input is editable — rate, portfolio, draw, rate path, taxes, and more. Hover the (i) icons for plain-language help; results update live.' },
  { el: '[data-tour="rate"]', title: 'Your rate', description: 'Enter the base rate your line floats on (pre-filled with the latest SOFR) and the spread your lender quoted you. Together they are your APR, so any lender fits — save one scenario per lender to compare them.' },
  { el: '[data-tour="views"]', title: 'Four views', description: 'Dashboard (here), Compare (your saved scenarios side by side), Analysis (solver, sensitivity, Monte Carlo), and Stress (a live market-drop what-if).' },
  { el: '[data-tour="kpis"]', title: 'Headline numbers', description: 'APR, net worth, worst LTV, call cushion, break-even, and the estate difference. Risk cards turn red when things get tight.' },
  { el: '[data-tour="charts"]', title: 'Charts', description: 'Net worth, borrow-vs-sell, LTV with the danger zone, estate, and more. Each has a "How to read this".' },
  // Anchored below-right rather than left: this target sits at the top-right of the sticky header,
  // and driver.js's default placement put the popover to its left, clipping the tail of the page
  // title. Hanging it under the button keeps the title clear and points the arrow straight at it.
  { el: '[data-tour="scenarios"]', title: 'Save & compare', description: 'Save unlimited scenarios, compare up to three, and export/import them as a JSON backup. Everything stays in your browser.', side: 'bottom', align: 'end' },
];

/** Breathing room between the sticky header and the highlighted element. */
const TOUR_SCROLL_GAP = 12;

/** Height of the `sticky top-0` header currently covering the top of the viewport. */
function headerOffset(): number {
  const header = document.querySelector('header');
  return header ? header.getBoundingClientRect().height + TOUR_SCROLL_GAP : 0;
}

/**
 * Publish the sticky header's height as `--tour-scroll-margin`, which `[data-tour]` elements use
 * as `scroll-margin-top` (see `src/index.css`).
 *
 * Measured per step rather than once, because the header grows when its tagline and disclaimer
 * wrap, and the viewport can change size mid-tour.
 */
function syncScrollMargin(): void {
  document.documentElement.style.setProperty('--tour-scroll-margin', `${Math.round(headerOffset())}px`);
}

/**
 * Put the TOP of the highlighted element just below the sticky header.
 *
 * driver.js does its own scrolling, and two of its choices are wrong for this layout: it centres
 * anything shorter than the viewport (so a short target lands mid-screen, or behind the header
 * once the header is accounted for), and it skips scrolling entirely for anything already fully
 * in view. For a tall section like the charts grid the result was a step titled "Charts" showing
 * the tail of one chart — its axis and legend — with the section's top far above the viewport.
 *
 * A step describes something, so it should start at that thing's beginning. Scrolling here, after
 * driver has finished, makes every step land the same way regardless of where the previous one
 * left the page.
 */
function alignToTop(element?: Element): void {
  const header = document.querySelector('header');
  // The scenarios button lives INSIDE the sticky header; it is always on screen, and scrolling
  // to it would jump the page to the top for no reason.
  if (!element || (header && header.contains(element))) return;
  const top = element.getBoundingClientRect().top + window.scrollY - headerOffset();
  window.scrollTo({ top: Math.max(0, Math.round(top)), behavior: 'auto' });
}

/** Launch the spotlight tour over whichever target elements are present (§10.8). */
export function runTour(onDone?: () => void): void {
  const steps = TOUR_STEPS.filter((s) => document.querySelector(s.el)).map((s) => ({
    element: s.el,
    popover: { title: s.title, description: s.description, ...(s.side ? { side: s.side, align: s.align } : {}) },
  }));
  syncScrollMargin();
  driver({
    showProgress: true,
    steps,
    onHighlightStarted: syncScrollMargin,
    onHighlighted: alignToTop,
    onDestroyed: () => {
      document.documentElement.style.removeProperty('--tour-scroll-margin');
      onDone?.();
    },
  }).drive();
}
